import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, RouteProps } from "react-router-dom";
/* PLOP_ROUTE_IMPORT */
import { Login } from "./pages/Login";
import { ResetPassword } from "./pages/ResetPassword";
import { Redirection } from "./pages/Redirection";
import { Loading, ExternalLink } from "./components/elements";
import { useUserContext } from "./context/user/AuthContext";
import { checkIsPlus } from "./constants/userGroups";

// Lazy-loaded pages (code splitting por rota)
const Home = React.lazy(() => import("./pages/Home").then(m => ({ default: m.Home })));
const Courses = React.lazy(() => import("./pages/Courses").then(m => ({ default: m.Courses })));
const CourseDetail = React.lazy(() => import("./pages/CourseDetail").then(m => ({ default: m.CourseDetail })));
const StudentProfile = React.lazy(() => import("./pages/StudentProfile").then(m => ({ default: m.StudentProfile })));
const ArticleDetail = React.lazy(() => import("./pages/ArticleDetail").then(m => ({ default: m.ArticleDetail })));
const Articles = React.lazy(() => import("./pages/Articles").then(m => ({ default: m.Articles })));
const Comunity = React.lazy(() => import("./pages/Comunity").then(m => ({ default: m.Comunity })));
const ComunityDetail = React.lazy(() => import("./pages/ComunityDetail").then(m => ({ default: m.ComunityDetail })));
const CommentDetail = React.lazy(() => import("./pages/CommentDetail").then(m => ({ default: m.CommentDetail })));
const MultimediaDetails = React.lazy(() => import("./pages/MultimediaDetails").then(m => ({ default: m.MultimediaDetails })));
const Multimidias = React.lazy(() => import("./pages/Multimidias").then(m => ({ default: m.Multimidias })));
const Downloads = React.lazy(() => import("./pages/Downloads").then(m => ({ default: m.Downloads })));
const Ebooks = React.lazy(() => import("./pages/Ebooks").then(m => ({ default: m.Ebooks })));
const NotificationList = React.lazy(() => import("./pages/NotificationList").then(m => ({ default: m.NotificationList })));
const SearchResultsPage = React.lazy(() => import("./pages/SearchResultsPage"));
const BancoDeTalentos = React.lazy(() => import("./pages/BancoDeTalentos").then(m => ({ default: m.BancoDeTalentos })));
const Favoritos = React.lazy(() => import("./pages/Favoritos").then(m => ({ default: m.Favoritos })));
const HubPosGraduacao = React.lazy(() => import("./pages/HubPosGraduacao").then(m => ({ default: m.HubPosGraduacao })));
const SejaPlus = React.lazy(() => import("./pages/SejaPlus").then(m => ({ default: m.SejaPlus })));
const Contratos = React.lazy(() => import("./pages/Contratos").then(m => ({ default: m.Contratos })));
const PublicProfile = React.lazy(() => import("./pages/PublicProfile").then(m => ({ default: m.PublicProfile })));

interface ICustomRouteProps {
    isPublic?: boolean;
    isPlus?: boolean;
    path: string | string[];
    element: React.ReactElement;
}

const CustomRoute = ({ isPublic, isPlus, path, element }: ICustomRouteProps) => {
    const { loading, authenticated, userData } = useUserContext();

    if (loading) {
        return <Loading />;
    }

    if (isPlus && authenticated) {
        const hasPlusAccess = checkIsPlus(userData.grupos);
        if (!hasPlusAccess) {
            return <Navigate to="/cursos" replace />;
        }
    }

    if (!isPublic && !authenticated) {
        if (window.location.pathname !== "/") {
            localStorage.setItem("redirectUrl", window.location.pathname);
        }
        return <Navigate to="/" replace />;
    }

    return element;
};

export default function AppRoutes() {
    return (
        <BrowserRouter>
            <Suspense fallback={<Loading />}>
            <Routes>
                <Route path="/" element={<CustomRoute isPublic path="/" element={<Login />} />} />
                <Route path="/esqueci-senha" element={<CustomRoute isPublic path="/esqueci-senha" element={<ResetPassword />} />} />
                {/* PLOP_INJECT_ROUTE */}
                <Route path="/banco-de-talentos" element={<CustomRoute path="/banco-de-talentos" element={<BancoDeTalentos />} />} />
                <Route path="/Favoritos" element={<CustomRoute path="/Favoritos" element={<Favoritos />} />} />
                <Route path="/pos-graduacao" element={<CustomRoute path="/pos-graduacao" element={<HubPosGraduacao />} />} />
                <Route path="/seja-plus" element={<CustomRoute path="/seja-plus" element={<SejaPlus />} />} />
                <Route path="/contratos/:id" element={<CustomRoute path="/contratos/:id" element={<Contratos />} />} />
                <Route path="/perfil/:id" element={<CustomRoute path="/perfil/:id" element={<PublicProfile />} />} />
                <Route path="/redirection" element={<CustomRoute path="/redirection" element={<Redirection />} />} />
                <Route path="/notificacoes" element={<CustomRoute path="/notificacoes" element={<NotificationList />} />} />
                <Route path="/meu-perfil" element={<CustomRoute path="/meu-perfil" element={<StudentProfile />} />} />
                <Route path="/cursos" element={<CustomRoute path="/cursos" element={<Courses />} />} />
                <Route path="/cursos/:slug" element={<CustomRoute path="/cursos/:slug" element={<CourseDetail />} />} />
                <Route path="/cursos/:slug/:aulaid" element={<CustomRoute path="/cursos/:slug/:aulaid" element={<CourseDetail />} />} />
                <Route
                    path="/cursos/:slug/:aulaid/:commentid"
                    element={<CustomRoute path="/cursos/:slug/:aulaid/:commentid" element={<CourseDetail />} />}
                />
                <Route
                    path="/detalhe-comentario/:debateId/:commentId"
                    element={<CustomRoute path="/detalhe-comentario/:debateId/:commentId" element={<CommentDetail />} />}
                />
                <Route path="/dashboard" element={<CustomRoute isPlus path="/dashboard" element={<Home />} />} />
                <Route path="/materiais" element={<CustomRoute isPlus path="/materiais" element={<Downloads />} />} />
                <Route
                    path="/materiais/categoria/:category"
                    element={<CustomRoute isPlus path="/materiais/categoria/:category" element={<Downloads />} />}
                />
                <Route path="/ebooks" element={<CustomRoute path="/ebooks" element={<Ebooks />} />} />
                <Route
                    path="/ebooks/categoria/:category"
                    element={<CustomRoute path="/ebooks/categoria/:category" element={<Ebooks />} />}
                />
                <Route path="/artigos" element={<CustomRoute isPlus path="/artigos" element={<Articles />} />} />
                <Route
                    path="/artigos/categoria/:category"
                    element={<CustomRoute isPlus path="/artigos/categoria/:category" element={<Articles />} />}
                />
                <Route path="/artigo/:slug" element={<CustomRoute isPlus path="/artigo/:slug" element={<ArticleDetail />} />} />
                <Route path="/multimidias" element={<CustomRoute isPlus path="/multimidias" element={<Multimidias />} />} />
                <Route path="/serie/:slug" element={<CustomRoute isPlus path="/serie/:slug" element={<MultimediaDetails />} />} />
                <Route path="/serie/:slug/:id" element={<CustomRoute isPlus path="/serie/:slug/:id" element={<MultimediaDetails />} />} />
                <Route path="/noticias" element={<CustomRoute isPlus path="/noticias" element={<Comunity />} />} />
                <Route path="/noticias/categoria/:slug" element={<CustomRoute isPlus path="/noticias/categoria/:slug" element={<Comunity />} />} />
                <Route path="/noticias/:comunityId" element={<CustomRoute isPlus path="/noticias/:comunityId" element={<ComunityDetail />} />} />
                <Route path="/buscar" element={<CustomRoute path="/buscar" element={<SearchResultsPage />} />} />
                <Route
                    path="/sociautism"
                    element={<CustomRoute path="/sociautism" element={<ExternalLink url="https://sociautism.ciaautismo.com.br/" />} />}
                />
                <Route
                    path="/indicacao"
                    element={
                        <CustomRoute
                            path="/indicacao"
                            element={
                                <ExternalLink url="http://cursoaba.academiadoautismo.com.br/indicacao/?bl_ref=DW306" redirectTo="/redirection" />
                            }
                        />
                    }
                />
            </Routes>
            </Suspense>
        </BrowserRouter>
    );
}
